import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import * as helpers from '@turf/helpers';

import {
  ADD_FEATURE_TYPE,
  FEEDBACK_WIZARD_STEP,
} from '../../../common/utils/helpers/routes';
import { round } from '../../../common/utils/helpers/math';
import {
  markerDragged,
  markerInitialized,
  markerPositionCountryCodeChanged,
  selectLocation,
  selectLongPressLocation,
  longPressLocationChanged,
  markerRemoved,
} from '../../../common/store/item-edit';
import { useMobile } from '../../../common/components/responsive/Responsive';
import { useGlMap } from '../../contexts/context';
import { itemMobileCenteringAnimationOptions } from '../../utils/constants/constants';
import { selectZoom } from '../../store';
import { useRetryingAnimationRunner } from '../../utils/hooks/useRetryingAnimationRunner';
import {
  sidepanelPadding,
  useMapPadding,
} from '../../utils/hooks/useMapPadding';
import {
  checkZoomEditable,
  getLocationCountryCode,
} from '../../utils/helpers/utils';
import {
  OMIT_POLYGON_TYPES,
  createOmitPolygons,
} from '../../utils/helpers/omitPolygonsUtils';
import { performInterruptionSafeAnimation } from '../../utils/helpers/animationUtils';
import { useMapWaterPolygon } from '../../contexts/mapWaterPolygonContext';
import { useMapBoundsPolygon } from '../../utils/hooks/useMapBoundsPolygon';
import { IconName } from '../../../common/components/icon/Icon.tsx';

interface RouteMatch {
  wizardStep;
  featureType;
}

export const useMapItemAdd = () => {
  const glMap = useGlMap();
  const animationRunner = useRetryingAnimationRunner();
  useMapPadding({ animationRunner });
  const isMobile = useMobile();
  const match = useRouteMatch<RouteMatch>();
  const dispatch = useDispatch();

  // flag used on mobile to show that location is selectable depending on the wizard step
  const locationSelectable = useMemo(() => {
    const { wizardStep, featureType } = match.params;

    return (
      !isMobile ||
      (featureType === ADD_FEATURE_TYPE.business &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.road &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.speedLimit &&
        wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
      (featureType === ADD_FEATURE_TYPE.incident &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.clearance &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.address &&
        wizardStep === FEEDBACK_WIZARD_STEP.feedback)
    );
  }, [match, isMobile]);

  const zoom = useSelector(selectZoom);

  const markerLocation = useSelector(selectLocation);
  const markerLocationRef = useRef();
  markerLocationRef.current = markerLocation;

  const overlayParams: { icon: IconName; text: string } | undefined =
    useMemo(() => {
      const zoomAllowEditLocation = checkZoomEditable(zoom);

      if (!markerLocation && locationSelectable && !zoomAllowEditLocation) {
        return {
          icon: 'magnifying-glass-add',
          text: 'Zoom in to add object',
        };
      }

      if (markerLocation && !zoomAllowEditLocation) {
        return {
          icon: 'magnifying-glass-add',
          text: 'Zoom in to edit object location',
        };
      }
    }, [markerLocation, locationSelectable, zoom]);

  const handleDragEnd = useCallback(
    (location) => {
      const cutLocation = location.map(round.fp());

      if (!isEqual(cutLocation, markerLocationRef.current)) {
        dispatch(markerDragged(cutLocation));

        const locationCountryCode = getLocationCountryCode(glMap, location);
        if (locationCountryCode) {
          dispatch(markerPositionCountryCodeChanged(locationCountryCode));
        }
      }
    },
    [dispatch, glMap],
  );

  // remove marker initially
  useEffect(() => {
    dispatch(markerRemoved());
  }, [dispatch]);

  const longPressLocation = useSelector(selectLongPressLocation);
  // initializing marker with position on the map using long press location and clean it so it won't reinitialize marker
  useEffect(() => {
    if (longPressLocation) {
      dispatch(
        markerInitialized({
          location: longPressLocation,
          dragged: true,
        }),
      );
      dispatch(longPressLocationChanged(null));
    }
  }, [dispatch, longPressLocation]);

  const mapWaterPolygon = useMapWaterPolygon();
  const boundsPolygon = useMapBoundsPolygon(glMap);
  const correctCoordinates = useMemo(() => {
    if (!mapWaterPolygon || !boundsPolygon) {
      return () => null;
    }

    return createOmitPolygons([
      {
        type: OMIT_POLYGON_TYPES.allow,
        geometry: boundsPolygon,
      },
      {
        type: OMIT_POLYGON_TYPES.restrict,
        geometry: mapWaterPolygon,
      },
    ]);
  }, [mapWaterPolygon, boundsPolygon]);

  // (re)initializing marker with position on the map on zoom change
  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (checkZoomEditable(zoom)) {
      if (!markerLocation) {
        const { height: mapCanvasHeight, width: mapCanvasWidth } = glMap
          .getCanvas()
          .getBoundingClientRect();

        const mapCenter = isMobile
          ? glMap.getCenter().toArray()
          : glMap
              .unproject([
                (mapCanvasWidth + sidepanelPadding.left) / 2,
                mapCanvasHeight / 2,
              ])
              .toArray();

        const markerInitialLocation = correctCoordinates(mapCenter)?.map(
          round.fp(),
        );

        if (!markerInitialLocation) {
          return;
        }

        dispatch(
          markerInitialized({
            location: markerInitialLocation,
            dragged: true,
          }),
        );
      }
    }
  }, [dispatch, glMap, isMobile, markerLocation, correctCoordinates, zoom]);

  const zoomRef = useRef<number>(0);
  zoomRef.current = zoom;
  // remove marker if it's location is on water
  useEffect(() => {
    if (
      !locationSelectable ||
      !checkZoomEditable(zoomRef.current) ||
      !markerLocation ||
      !boundsPolygon ||
      !booleanPointInPolygon(helpers.point(markerLocation), boundsPolygon)
    ) {
      return;
    }

    const correctedMarkerCoordinates = correctCoordinates(markerLocation);

    if (correctedMarkerCoordinates) {
      handleDragEnd(correctedMarkerCoordinates);
    } else {
      dispatch(markerRemoved());
    }
  }, [
    dispatch,
    handleDragEnd,
    markerLocation,
    correctCoordinates,
    boundsPolygon,
    locationSelectable,
  ]);

  // animate map center to be on marker location on mobile
  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (
      isMobile &&
      locationSelectable &&
      markerLocation &&
      !isEqual(glMap.getCenter().toArray(), markerLocation)
    ) {
      return animationRunner(
        (animationDuration) =>
          performInterruptionSafeAnimation(
            glMap,
            (duration) =>
              glMap.easeTo({
                center: markerLocation,
                ...itemMobileCenteringAnimationOptions,
                duration,
              }),
            animationDuration,
          ),
        { animationDuration: itemMobileCenteringAnimationOptions.duration },
      );
    }
  }, [glMap, isMobile, locationSelectable, markerLocation, animationRunner]);

  return {
    markerLocation,
    locationSelectable,
    isMobile,
    handleDragEnd,
    correctCoordinates,
    overlayParams,
  };
};
